import React from 'react';
import { useFamily } from '../../context/FamilyContext';
import { usePermissions } from '../../hooks/usePermissions';
import { DOCUMENT_CATEGORIES } from '@coparent/shared/src/utils/constants';
import ChildDots from '../common/ChildDots';
import { HiEye, HiDownload, HiTrash, HiDocumentText, HiPhotograph } from 'react-icons/hi';
import { deleteDocument } from '@coparent/shared/src/services/documentService';
import toast from 'react-hot-toast';

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (timestamp) => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const getFileIcon = (fileType) => {
  if (fileType?.startsWith('image/')) return <HiPhotograph className="text-2xl text-meadow-500" />;
  return <HiDocumentText className="text-2xl text-canopy-500" />;
};

const isPreviewable = (fileType) => {
  return fileType?.startsWith('image/') || fileType === 'application/pdf';
};

const DocumentCard = ({ document, onPreview }) => {
  const { familyId, childrenMap } = useFamily();
  const { canDelete } = usePermissions();
  const categoryLabel = DOCUMENT_CATEGORIES.find(c => c.value === document.category)?.label || document.category;

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${document.name}"? This cannot be undone.`)) return;
    try {
      await deleteDocument(familyId, document.id, document.storagePath);
      toast.success('Document deleted');
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  return (
    <div className="card-hover p-4">
      <div className="flex items-start gap-3">
        {getFileIcon(document.fileType)}
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-ink-900 text-sm truncate">{document.name}</p>
          <p className="text-xs text-ink-400 mt-0.5">
            {categoryLabel} &middot; {formatFileSize(document.fileSize)}
          </p>
          <p className="text-xs text-ink-300 mt-0.5">
            {document.uploadedByName} &middot; {formatDate(document.createdAt)}
          </p>
          {document.childIds?.length > 0 && (
            <div className="mt-1.5">
              <ChildDots childIds={document.childIds} childrenMap={childrenMap} showNames />
            </div>
          )}
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2 pt-3 border-t border-surface-100">
        {isPreviewable(document.fileType) && (
          <button
            onClick={() => onPreview(document)}
            className="w-7 h-7 rounded-lg flex items-center justify-center text-ink-300 hover:text-canopy-600 hover:bg-canopy-50 transition-colors"
            title="Preview"
          >
            <HiEye className="text-sm" />
          </button>
        )}
        <a
          href={document.storageUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="w-7 h-7 rounded-lg flex items-center justify-center text-ink-300 hover:text-ink-600 hover:bg-surface-100 transition-colors"
          title="Download"
        >
          <HiDownload className="text-sm" />
        </a>
        {canDelete && (
          <button
            onClick={handleDelete}
            className="ml-auto w-7 h-7 rounded-lg flex items-center justify-center text-ink-300 hover:text-accent-600 hover:bg-accent-50 transition-colors"
            title="Delete"
          >
            <HiTrash className="text-sm" />
          </button>
        )}
      </div>
    </div>
  );
};

export default DocumentCard;
